/**
 * Unified Analytics & Key Event Tracking for Venus Global Technology
 *
 * Fully addresses Fix 1 [P0]:
 * "Fire a generate_lead key event on form submit, plus tel:, mailto: and WhatsApp click events"
 *
 * Supported destinations:
 * - Google Tag Manager dataLayer (Custom Events: generate_lead, form_submission, contact_click)
 * - Google Analytics 4 (GA4 Key Event: generate_lead)
 * - OpenAI Ads Pixel (window.oaiq: lead_created)
 */

/**
 * Fires the GA4 generate_lead key event for form submissions
 */
export function trackLeadSubmission({
  formName = 'Contact Form',
  leadType = 'general_inquiry',
  company = '',
  projectType = '',
  additionalParams = {}
} = {}) {
  try {
    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];

    // 1. Push GA4 Recommended Key Event: generate_lead
    window.dataLayer.push({
      event: 'generate_lead',
      form_name: formName,
      lead_type: leadType,
      project_type: projectType || undefined,
      company: company || undefined,
      value: 1,
      currency: 'USD',
      ...additionalParams
    });

    // 2. Also push form_submission for existing GTM tags
    window.dataLayer.push({
      event: 'form_submission',
      form_id: formName.toLowerCase().replace(/[^a-z0-9]+/g, '_'),
      form_name: formName,
      lead_type: leadType
    });

    // 3. Direct gtag fallback if initialized globally
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'generate_lead', {
        event_category: 'Leads',
        event_label: formName,
        lead_type: leadType,
        value: 1,
        currency: 'USD'
      });
      window.gtag('event', 'form_submit', {
        event_category: 'Form',
        event_label: formName
      });
    }

    // 4. OpenAI Ads Pixel (standard customer_action event)
    if (typeof window.oaiq === 'function') {
      try {
        window.oaiq('measure', 'lead_created', {
          type: 'customer_action'
        });
      } catch (oaiErr) {
        // Non-critical pixel warning suppression
      }
    }

    console.info(`[Analytics] generate_lead fired for form: ${formName}`);
  } catch (err) {
    console.warn('[Analytics] Failed to track lead submission:', err);
  }
}

/**
 * Fires the GA4 generate_lead key event for direct contact link clicks (tel:, mailto:, WhatsApp)
 */
export function trackContactClick({
  type = 'phone', // 'phone' | 'email' | 'whatsapp'
  destination = '',
  label = '',
  additionalParams = {}
} = {}) {
  try {
    if (typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];

    const normalizedType =
      type === 'tel' || type === 'phone'
        ? 'phone_call'
        : type === 'mailto' || type === 'email'
        ? 'email_inquiry'
        : 'whatsapp_chat';

    const method =
      type === 'tel' || type === 'phone'
        ? 'tel'
        : type === 'mailto' || type === 'email'
        ? 'mailto'
        : 'whatsapp';

    const action =
      normalizedType === 'phone_call'
        ? 'click_to_call'
        : normalizedType === 'email_inquiry'
        ? 'click_to_email'
        : 'click_to_whatsapp';

    // 1. Push GA4 Key Event generate_lead to dataLayer
    window.dataLayer.push({
      event: 'generate_lead',
      lead_type: normalizedType,
      method: method,
      contact_destination: destination,
      event_category: 'Contact',
      event_action: action,
      event_label: label || destination,
      value: 1,
      currency: 'USD',
      ...additionalParams
    });

    // 2. Also push specific GA4 event (phone_call, email_inquiry, or whatsapp_chat)
    window.dataLayer.push({
      event: normalizedType,
      lead_type: normalizedType,
      method: method,
      contact_destination: destination,
      value: 1,
      currency: 'USD'
    });

    // 3. Also push contact_click event for GTM interaction triggers
    window.dataLayer.push({
      event: 'contact_click',
      contact_type: normalizedType,
      contact_method: method,
      contact_destination: destination
    });

    // 4. Direct gtag fallback
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'generate_lead', {
        event_category: 'Contact',
        event_action: action,
        event_label: label || destination,
        lead_type: normalizedType,
        method: method,
        value: 1,
        currency: 'USD'
      });
      window.gtag('event', normalizedType, {
        event_category: 'Contact',
        event_label: label || destination,
        value: 1,
        currency: 'USD'
      });
      window.gtag('event', action, {
        event_category: 'Contact',
        event_label: label || destination
      });
    }

    // 4. OpenAI Ads Pixel measurement
    if (typeof window.oaiq === 'function') {
      try {
        window.oaiq('measure', 'lead_created', {
          type: 'customer_action'
        });
      } catch (e) {}
    }

    console.info(`[Analytics] generate_lead fired for ${normalizedType}: ${destination}`);
  } catch (err) {
    console.warn('[Analytics] Failed to track contact click:', err);
  }
}

/**
 * Initializes automatic global event tracking on the document
 * Listens for all clicks on tel:, mailto:, and WhatsApp links, and generic form submissions.
 */
export function initGlobalLeadTracking() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Prevent multiple bindings
  if (window.__vgt_lead_tracking_initialized) return;
  window.__vgt_lead_tracking_initialized = true;

  // 1. Delegated click listener for tel:, mailto:, and WhatsApp
  document.addEventListener('click', (event) => {
    try {
      const link = event.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href') || '';
      const text = (link.innerText || link.getAttribute('aria-label') || '').trim();

      // Check for phone call clicks (tel: protocol)
      if (href.startsWith('tel:')) {
        trackContactClick({
          type: 'phone',
          destination: href.replace(/^tel:/, '').trim(),
          label: text || href
        });
        return;
      }

      // Check for email clicks (mailto: protocol)
      if (href.startsWith('mailto:')) {
        trackContactClick({
          type: 'email',
          destination: href.replace(/^mailto:/, '').split('?')[0].trim(),
          label: text || href
        });
        return;
      }

      // Check for WhatsApp clicks
      const isWhatsApp =
        href.includes('wa.me') ||
        href.includes('whatsapp.com') ||
        link.classList.contains('v2-floating-whatsapp-btn') ||
        (link.getAttribute('aria-label') && link.getAttribute('aria-label').toLowerCase().includes('whatsapp'));

      if (isWhatsApp) {
        trackContactClick({
          type: 'whatsapp',
          destination: href,
          label: text || 'WhatsApp Chat'
        });
      }
    } catch (e) {
      // Suppress event listener exceptions
    }
  }, { passive: true });

  // 2. Delegated form submit listener to ensure EVERY form generates a lead event
  document.addEventListener('submit', (event) => {
    try {
      const form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      // Prevent duplicate tracking if the form already handled tracking explicitly in the last 1500ms
      const now = Date.now();
      if (form.__last_tracked && now - form.__last_tracked < 1500) {
        return;
      }
      form.__last_tracked = now;

      const formId = form.getAttribute('id') || form.getAttribute('name') || '';
      // Skip internal admin/login forms if any
      if (formId.includes('login') || formId.includes('admin')) return;

      const formTitle =
        form.querySelector('h1, h2, h3, .form-title')?.innerText ||
        formId ||
        'Website Inbound Form';

      trackLeadSubmission({
        formName: formTitle,
        leadType: 'form_submission'
      });
    } catch (e) {
      // Suppress
    }
  }, { passive: true });

  // 3. Bot detection & GA4 traffic segmentation (Fix 12: filters ~30% bot sessions)
  initBotSegmentation();

  console.info('[Analytics] Global lead tracking initialized for forms, tel, mailto, and WhatsApp.');
}

/**
 * Detects automated/headless bot traffic and pushes segmentation tags to GA4/GTM
 * Addresses Fix 12: GA4 segment without bots
 */
export function initBotSegmentation() {
  if (typeof window === 'undefined') return;
  try {
    let isBot = false;
    if (navigator.webdriver) isBot = true;
    if (window._phantom || window.__nightmare || window.callPhantom) isBot = true;
    const ua = (navigator.userAgent || '').toLowerCase();
    if (/bot|crawler|spider|headless|crawl|slurp|facebookexternalhit|whatsapp|google-read-aloud|lighthouse|ptst|bytespider|gptbot|claudebot|semrushbot|ahrefsbot|mj12bot|dotbot|petalbot/i.test(ua)) isBot = true;
    if (window.outerWidth === 0 && window.outerHeight === 0) isBot = true;
    if (!navigator.languages || navigator.languages.length === 0) isBot = true;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'traffic_segmentation',
      traffic_type: isBot ? 'bot' : 'real',
      visitor_segment: isBot ? 'suspected_bot' : 'human_visitor',
      is_bot: isBot ? 'yes' : 'no',
      bot_status: isBot ? 'bot' : 'clean'
    });

    if (typeof window.gtag === 'function') {
      window.gtag('set', 'user_properties', {
        traffic_type: isBot ? 'bot' : 'real',
        visitor_segment: isBot ? 'suspected_bot' : 'human_visitor',
        is_bot: isBot ? 'yes' : 'no'
      });
    }
  } catch (e) {}
}

